import React, { useState, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Easing } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons as Icon } from '@expo/vector-icons';
import { Colors } from '../constants/colors';
import { useWallet } from '../context/WalletContext';
import RewardPin from '../components/RewardPin';

var GIFT_REWARDS = [
  { s: 'Tim Hortons', a: '$10', c: '#C8102E' },
  { s: 'Starbucks', a: '$15', c: '#00704A' },
  { s: 'Walmart', a: '$20', c: '#0071CE' },
  { s: 'Subway', a: '$10', c: '#008C15' },
  { s: '7-Eleven', a: '$10', c: '#FF6600' },
  { s: 'Shoppers', a: '$50', c: '#E01A2B' },
  { s: 'Amazon', a: '$25', c: '#FF9900' },
  { s: 'Safeway', a: '$20', c: '#E8352D' },
];

var SCREEN_REWARDS = [
  { app: 'TikTok', min: 30 },
  { app: 'Instagram', min: 20 },
  { app: 'YouTube', min: 45 },
  { app: 'Candy Crush', min: 30 },
  { app: 'Facebook', min: 20 },
  { app: 'Spotify', min: 60 },
];

export default function SpinnerScreen({ navigation }) {
  var tabState = useState('gifts');
  var spinTab = tabState[0];
  var setSpinTab = tabState[1];
  var spinningState = useState(false);
  var spinning = spinningState[0];
  var setSpinning = spinningState[1];
  var resultState = useState(null);
  var result = resultState[0];
  var setResult = resultState[1];

  var spinAnim = useRef(new Animated.Value(0)).current;
  var totalRotation = useRef(0);
  var { addReward } = useWallet();

  var rewards = spinTab === 'gifts' ? GIFT_REWARDS : SCREEN_REWARDS;
  var cnt = rewards.length;
  var aPer = 360 / cnt;

  function doSpin() {
    if (spinning) return;
    setSpinning(true);
    setResult(null);

    var winIdx = Math.floor(Math.random() * cnt);
    var extra = 360 * (5 + Math.random() * 3);
    var target = totalRotation.current + extra + winIdx * aPer;
    totalRotation.current = target;

    Animated.timing(spinAnim, {
      toValue: target,
      duration: 3500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(function() {
      setSpinning(false);
      setResult(Object.assign({}, rewards[winIdx], { idx: winIdx }));
    });
  }

  function claim() {
    if (result) {
      if (spinTab === 'gifts') {
        addReward({ type: 'giftcard', store: result.s, amount: result.a, color: result.c });
      } else {
        addReward({ type: 'screentime', app: result.app, minutes: result.min });
      }
    }
    navigation.goBack();
  }

  var spinInterpolation = spinAnim.interpolate({
    inputRange: [0, 360],
    outputRange: ['0deg', '360deg'],
  });

  var PIN_RADIUS = 108;
  var WHEEL_SIZE = 300;
  var CENTER = WHEEL_SIZE / 2;

  return (
    <View style={s.container}>
      <SafeAreaView style={s.safe}>
        <TouchableOpacity style={s.closeBtn} onPress={function() { navigation.goBack(); }}>
          <Icon name="close" size={24} color="#fff" />
        </TouchableOpacity>

        <Text style={s.title}>Claim Reward</Text>

        <View style={s.tabRow}>
          <TouchableOpacity
            style={[s.tab, spinTab === 'gifts' && s.tabActive]}
            onPress={function() { if (!spinning) { setSpinTab('gifts'); setResult(null); } }}
          >
            <Text style={[s.tabText, spinTab === 'gifts' && s.tabTextActive]}>Gift Cards</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[s.tab, spinTab === 'screen' && s.tabActive]}
            onPress={function() { if (!spinning) { setSpinTab('screen'); setResult(null); } }}
          >
            <Text style={[s.tabText, spinTab === 'screen' && s.tabTextActive]}>Screen Time</Text>
          </TouchableOpacity>
        </View>

        {/* Spinner Wheel */}
        <View style={[s.wheelContainer, { width: WHEEL_SIZE, height: WHEEL_SIZE }]}>
          <Animated.View style={[s.wheel, { width: WHEEL_SIZE, height: WHEEL_SIZE, transform: [{ rotate: spinInterpolation }] }]}>
            {rewards.map(function(r, i) {
              var angleDeg = i * aPer;
              var angleRad = (angleDeg - 90) * (Math.PI / 180);
              var cx = CENTER + Math.cos(angleRad) * PIN_RADIUS;
              var cy = CENTER + Math.sin(angleRad) * PIN_RADIUS;
              var pinColor = spinTab === 'gifts' ? Colors.pinBlue : Colors.pinPurple;

              return (
                <View key={i} style={{
                  position: 'absolute',
                  left: cx - 31,
                  top: cy - 38,
                  width: 62,
                  height: 82,
                  transform: [{ rotate: (angleDeg + 180) + 'deg' }],
                  zIndex: cnt - i,
                }}>
                  <RewardPin
                    isGift={spinTab === 'gifts'}
                    label={spinTab === 'gifts' ? r.s : r.app}
                    value={spinTab === 'gifts' ? r.a : r.min + 'm'}
                    color={spinTab === 'gifts' ? r.c : Colors.screenTime}
                    pinColor={pinColor}
                    size="small"
                  />
                </View>
              );
            })}
          </Animated.View>

          {/* Result display */}
          {result && (
            <View style={s.resultWrap}>
              <RewardPin
                isGift={spinTab === 'gifts'}
                label={spinTab === 'gifts' ? result.s : result.app}
                value={spinTab === 'gifts' ? result.a : result.min + 'min'}
                color={spinTab === 'gifts' ? result.c : Colors.screenTime}
                pinColor={spinTab === 'gifts' ? Colors.pinBlue : Colors.pinPurple}
                size="large"
                glowing
              />
            </View>
          )}

          {/* Top pointer */}
          {!result && (
            <View style={s.pointer} />
          )}
        </View>

        {/* Dots */}
        <View style={s.dotsRow}>
          {rewards.map(function(_, i) {
            var isWinner = result && result.idx === i;
            return <View key={i} style={[s.dot, isWinner && s.dotActive]} />;
          })}
        </View>

        <TouchableOpacity
          style={[s.spinButton, spinning && s.spinButtonDisabled]}
          onPress={result ? claim : doSpin}
          disabled={spinning}
        >
          <Text style={s.spinButtonText}>{result ? 'Claim' : 'Spin'}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    </View>
  );
}

var s = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.overlayDark },
  safe: { flex: 1, alignItems: 'center', paddingTop: 20 },
  closeBtn: { position: 'absolute', top: 60, right: 20, zIndex: 20, padding: 4 },
  title: { color: '#fff', fontSize: 22, fontWeight: '700', marginBottom: 14 },
  tabRow: { flexDirection: 'row', gap: 10, marginBottom: 24 },
  tab: { paddingVertical: 8, paddingHorizontal: 22, borderRadius: 20, borderWidth: 2, borderColor: 'rgba(255,255,255,0.2)' },
  tabActive: { backgroundColor: 'rgba(255,255,255,0.2)', borderColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.4)', fontWeight: '600', fontSize: 13 },
  tabTextActive: { color: '#fff' },
  wheelContainer: { position: 'relative' },
  wheel: { position: 'relative' },
  resultWrap: { position: 'absolute', top: '50%', left: '50%', transform: [{ translateX: -55 }, { translateY: -70 }], zIndex: 20 },
  pointer: {
    position: 'absolute',
    top: -8,
    left: '50%',
    marginLeft: -12,
    width: 0,
    height: 0,
    borderLeftWidth: 12,
    borderRightWidth: 12,
    borderTopWidth: 18,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: Colors.accent,
    zIndex: 15,
  },
  dotsRow: { flexDirection: 'row', gap: 5, marginTop: 16, marginBottom: 12 },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.3)' },
  dotActive: { backgroundColor: '#fff' },
  spinButton: {
    paddingVertical: 14,
    paddingHorizontal: 70,
    borderRadius: 25,
    borderWidth: 2,
    borderColor: Colors.accent,
    marginTop: 6,
  },
  spinButtonDisabled: { opacity: 0.4 },
  spinButtonText: { color: Colors.accent, fontSize: 22, fontWeight: '700' },
});
